/**
 * Creator Academy: short (2–3 minute) how-to lessons on the creator skills brands pay for:
 * lighting, sound, editing, on camera, brand deals. Lessons are grouped into tracks and tracks
 * into stages that unlock in order. "Ask the Academy" matches a question to lessons.
 *
 * The catalogue (stages, tracks, lessons, their videos) lives in the database and is built in the
 * admin app; GET /api/athlete/academy sends the published part. This module holds its types and
 * the pure functions over a Catalogue, shared by the app and the server, which records progress.
 * The one thing kept in code is the list of lesson actions (LESSON_ACTIONS): the score knows how
 * to check each, so the admin app picks from them. A lesson's id is stable; never reuse one.
 */

/** A track's key: a slug the team chooses in the admin app */
export type TrackKey = string

export interface Track {
  key: TrackKey
  label: string
  stage: number // 1-based; stages unlock in order
  blurb: string
  /** Dot and bar colour */
  color: string
  /** Text colour that reads on white */
  ink: string
  /** Light tint for thumbnails and chips */
  tint: string
}

export interface Lesson {
  id: string
  track: TrackKey
  order: number // within the track
  title: string
  description: string
  durationSec: number
  /** The video the player plays (https) */
  src: string
  poster?: string | null
  publishedAt: string // ISO date
  tags: string[]
  /** What the athlete does after the lesson, and what it pays once watched and done */
  action?: { action: LessonActionId; points: number } | null
}

/** What athletes see: the published lessons, their tracks and the stage names */
export interface Catalogue {
  stages: Record<number, string>
  tracks: Track[]
  lessons: Lesson[]
}

export const EMPTY_CATALOGUE: Catalogue = { stages: {}, tracks: [], lessons: [] }

export const stageCount = (c: Catalogue) => Math.max(0, ...c.tracks.map((t) => t.stage))
export const trackByKey = (c: Catalogue, k: TrackKey) => c.tracks.find((t) => t.key === k) ?? null
export const lessonById = (c: Catalogue, id: string) => c.lessons.find((l) => l.id === id) ?? null

export const NEW_WITHIN_DAYS = 7
export const isNew = (l: Lesson) =>
  Date.now() - new Date(`${l.publishedAt}T00:00:00`).getTime() < NEW_WITHIN_DAYS * 86400000

export const fmtDuration = (s: number) => {
  const m = Math.floor(s / 60)
  const r = Math.round(s % 60)
  return `${m}:${String(r).padStart(2, '0')}`
}

// ─── Progress ─────────────────────────────────────────────────────────

/** Where an athlete is with one lesson (from GET /api/athlete/academy) */
export interface LessonProgress {
  /** Watched to the end, or marked done */
  completed: boolean
  /** Watched through (COMPLETE_AT of the video): what earns points */
  watched?: boolean
  position: number // seconds, for resume
  lastWatched?: string
}
export type Progress = Record<string, LessonProgress>

/** A lesson is watched through at 90% */
/**
 * A lesson counts as watched once the athlete has played all of it (Founder Brief: 100 percent of
 * the video). A couple of seconds of slack covers the gap between the player's last progress
 * ping and the end of the clip.
 */
export const COMPLETE_AT = 1
export const COMPLETE_SLACK_SEC = 2

// ─── Lesson actions (the Academy slice of the score) ────────────────────
//
// The Athios Score has four slices (athleteScore.ts). The Academy owns 30 of the 100 points.
// Each lesson below ends with one action. The action itself pays into its home slice (Profile,
// Activity or Platform record) whenever it is done; the Academy half pays once, when the lesson
// has been watched through AND the action is done, in either order. Lessons without an action
// teach but don't add points. Deal lessons pay when the deal finishes.
//
// These twelve actions (three or four per level, L1 to L4) are what the admin app can attach to
// a lesson. Keep the points on published lessons adding up to ACADEMY_MAX_POINTS.

export type LessonActionId =
  | 'profile'
  | 'themes'
  | 'first_post'
  | 'payouts'
  | 'plan_post'
  | 'socials'
  | 'publish'
  | 'posting_goal'
  | 'rate_card'
  | 'first_offer'
  | 'first_deal'
  | 'three_deals'

export interface LessonAction {
  action: LessonActionId
  /** What to do after the lesson, in the athlete's words */
  label: string
  /** Where doing it starts */
  to: string
  /** The Academy half: paid once the lesson is watched and the action is done */
  points: number
  /** Which level the lesson belongs to */
  level: 'L1' | 'L2' | 'L3' | 'L4'
}

/** The actions the admin app can attach to a lesson: what it asks, where it starts, its level */
export const LESSON_ACTIONS: Record<
  LessonActionId,
  { label: string; to: string; level: LessonAction['level'] }
> = {
  profile: { label: 'Finish your profile', to: '/setup/about', level: 'L1' },
  themes: { label: 'Pick your top five content themes', to: '/setup/themes', level: 'L1' },
  first_post: { label: 'Post your first planned post', to: '/calendar?plan=1', level: 'L1' },
  payouts: { label: 'Confirm your phone and add your bank', to: '/setup/payout', level: 'L1' },
  plan_post: { label: 'Plan a post on your calendar', to: '/calendar?plan=1', level: 'L2' },
  socials: { label: 'Connect your social accounts', to: '/setup/connect', level: 'L2' },
  publish: { label: 'Open your Content Shop', to: '/setup/publish', level: 'L2' },
  posting_goal: { label: 'Post 8 times in 30 days', to: '/calendar', level: 'L3' },
  rate_card: { label: 'Set your rate card', to: '/setup/rates', level: 'L3' },
  first_offer: { label: 'Accept your first offer', to: '/opportunities', level: 'L4' },
  first_deal: { label: 'Finish your first deal', to: '/deals', level: 'L4' },
  three_deals: { label: 'Finish three deals', to: '/deals', level: 'L4' },
}

export const LESSON_ACTION_IDS = Object.keys(LESSON_ACTIONS) as LessonActionId[]

/** The most the Academy can add to the score */
export const ACADEMY_MAX_POINTS = 30

/** A lesson's action in full, or null when it teaches without adding points */
export function lessonAction(lesson?: Lesson | null): LessonAction | null {
  const a = lesson?.action
  if (!a || !LESSON_ACTIONS[a.action]) return null
  return { action: a.action, points: a.points, ...LESSON_ACTIONS[a.action] }
}

/** Every lesson's action by lesson id (what the score reads) */
export function lessonActions(lessons: Lesson[]): Record<string, LessonAction> {
  const out: Record<string, LessonAction> = {}
  for (const l of lessons) {
    const a = lessonAction(l)
    if (a) out[l.id] = a
  }
  return out
}

// ─── Derived ─────────────────────────────────────────────────────────

export interface TrackStatus {
  track: Track
  lessons: Lesson[]
  done: number
  total: number
}

export function trackStatuses(c: Catalogue, p: Progress): TrackStatus[] {
  return c.tracks.map((track) => {
    const lessons = c.lessons
      .filter((l) => l.track === track.key)
      .sort((a, b) => a.order - b.order)
    const done = lessons.filter((l) => p[l.id]?.completed).length
    return { track, lessons, done, total: lessons.length }
  })
}

// The athlete's current stage = first stage with anything unfinished.
export function currentStage(c: Catalogue, p: Progress): number {
  const st = trackStatuses(c, p)
  const last = stageCount(c)
  for (let s = 1; s <= last; s++) {
    const tracks = st.filter((t) => t.track.stage === s)
    if (tracks.some((t) => t.done < t.total)) return s
  }
  return last
}

// Next lesson to play: in-progress first, else first unfinished in
// track/stage order, else the newest.
export function nextLesson(c: Catalogue, p: Progress, after?: Lesson): Lesson | null {
  const trackAt = (l: Lesson) => c.tracks.findIndex((t) => t.key === l.track)
  const ordered = [...c.lessons].sort(
    (a, b) =>
      (trackByKey(c, a.track)?.stage ?? 0) - (trackByKey(c, b.track)?.stage ?? 0) ||
      trackAt(a) - trackAt(b) ||
      a.order - b.order,
  )
  if (after) {
    const idx = ordered.findIndex((l) => l.id === after.id)
    const following = ordered.slice(idx + 1).find((l) => !p[l.id]?.completed)
    if (following) return following
  }
  const inProgress = ordered.find((l) => !p[l.id]?.completed && (p[l.id]?.position ?? 0) > 0)
  if (inProgress) return inProgress
  return ordered.find((l) => !p[l.id]?.completed) ?? ordered[ordered.length - 1] ?? null
}

export function searchLessons(c: Catalogue, q: string, track: TrackKey | 'all'): Lesson[] {
  const needle = q.trim().toLowerCase()
  return c.lessons.filter((l) => {
    if (track !== 'all' && l.track !== track) return false
    if (!needle) return true
    const hay = [l.title, l.description, trackByKey(c, l.track)?.label ?? '', ...l.tags]
      .join(' ')
      .toLowerCase()
    return needle.split(/\s+/).every((w) => hay.includes(w))
  })
}

// ─── Ask the Academy ─────────────────────────────────────────────────
// Natural-language in ("my audio sounds echoey", "how do I light a
// gym"), lessons out, with a one-line answer.
//
// DEMO ONLY, NOT FOR PRODUCTION: these are scripted keyword rules, not an assistant. The athlete
// app hides this behind VITE_FEATURE_ASK_ACADEMY (on for dev and marketplace only). The real
// version is an LLM over the lesson catalogue.

export interface AcademyAnswer {
  reply: string
  lessons: Lesson[]
}

const byIds = (c: Catalogue, ...ids: string[]) =>
  ids.map((id) => lessonById(c, id)).filter((l): l is Lesson => !!l)

const INTENTS: { match: RegExp; reply: string; ids: string[] }[] = [
  {
    match: /echo|bathroom|room|reverb|hollow|tinny/,
    reply: 'That echo is the room, not your phone. Fix the space first, then the mic.',
    ids: ['sound-1', 'sound-2'],
  },
  {
    match:
      /wind|outside|outdoor|crowd|noisy|stadium|field.*(audio|sound|mic)|(audio|sound|mic).*(field|track)/,
    reply:
      'Outdoor audio is its own problem — here’s wind, crowds, and when to just voiceover it.',
    ids: ['sound-3', 'sound-2'],
  },
  {
    match: /\bmic\b|microphone|lav|audio|sound|hear me|quiet|muffled/,
    reply: 'Three short ones on audio — start with the $20 mic, it’s the biggest jump.',
    ids: ['sound-2', 'sound-1', 'sound-3'],
  },
  {
    match: /gym|fluorescent|overhead|harsh|sun|glare|squint/,
    reply: 'Gym and field light you can’t control — here’s how to work with it.',
    ids: ['light-3', 'light-1'],
  },
  {
    match: /ring ?light|orange|raccoon|shadow under/,
    reply: 'Ring-light fixes: height, distance, warmth. Two minutes.',
    ids: ['light-2', 'light-1'],
  },
  {
    match: /light|dark|bright|window|glow|look (better|good) on camera|grainy/,
    reply:
      'Window light first — it’s free and beats most gear. Then the ring light and gym lessons.',
    ids: ['light-1', 'light-2', 'light-3'],
  },
  {
    match: /hook|first (few|three|3) seconds|scroll|retention|stop.*scroll|open(ing|er)/,
    reply: 'The first three seconds decide everything. Here’s how to open.',
    ids: ['edit-2', 'edit-1'],
  },
  {
    match: /caption|subtitle|text on screen/,
    reply: 'Captions that read on a phone without looking like captions.',
    ids: ['edit-3', 'edit-2'],
  },
  {
    match: /edit|capcut|trim|cut|speed|transition|pacing|too long/,
    reply: 'Editing, in order: CapCut basics → the hook → captions.',
    ids: ['edit-1', 'edit-2', 'edit-3'],
  },
  {
    match:
      /nervous|awkward|stumble|talk(ing)? to (the )?camera|on camera|one take|script|forget|energy|confident|eye contact/,
    reply: 'On-camera is a skill, not a personality. These two make it repeatable.',
    ids: ['cam-1', 'cam-2'],
  },
  {
    match:
      /brief|inquiry|brand (deal|ask|wants)|what (do|does) (the )?brand|placement|product in|too salesy|like an ad/,
    reply:
      'Brand deals: read the brief, place the product naturally, get the concept approved first time.',
    ids: ['brand-1', 'brand-2', 'brand-3'],
  },
  {
    match: /approv|concept|revision|rejected|feedback|redo/,
    reply: 'Getting a yes without a revision round comes down to the concept note.',
    ids: ['brand-3', 'brand-1'],
  },
  {
    match: /paid|pay|money|earn|quote|rate|payout|how much/,
    reply: 'How the money works — inquiries, quotes, approvals, payout.',
    ids: ['basics-4', 'brand-1'],
  },
  {
    match:
      /what (should|do) i post|content idea|what brands (buy|want)|format|kind of (post|video)/,
    reply: 'Start with what brands actually buy — it’s not the highlight reel.',
    ids: ['basics-2', 'basics-3'],
  },
  {
    match: /calendar|schedule|consisten|how often|organic|plan (my )?posts?|posting/,
    reply: 'Two posts a week is the goal. Here’s how to plan them around your deals.',
    ids: ['content-calendar', 'basics-2'],
  },
  {
    match: /start|begin|first post|new here|beginner|basics|how (does|do) (this|athios)/,
    reply: 'Start here — the four basics, about ten minutes total.',
    ids: ['basics-1', 'basics-2', 'basics-3', 'basics-4'],
  },
]

export function askAcademy(c: Catalogue, prompt: string): AcademyAnswer {
  const q = prompt.trim().toLowerCase()
  if (!q) return { reply: '', lessons: [] }
  const hit = INTENTS.find((i) => i.match.test(q))
  if (hit) return { reply: hit.reply, lessons: byIds(c, ...hit.ids) }
  const found = searchLessons(c, q, 'all')
  if (found.length)
    return {
      reply: `${found.length} lesson${found.length === 1 ? '' : 's'} match “${prompt.trim()}”.`,
      lessons: found,
    }
  return {
    reply: `Nothing on “${prompt.trim()}” yet. Try describing the problem — “my audio echoes”, “gym lighting”, “how do I open a video”.`,
    lessons: [],
  }
}

export const SUGGESTED_PROMPTS = [
  'My audio echoes',
  'Gym lighting',
  'First 3 seconds',
  'I got a brand brief',
]
